import { ComponentProps } from 'react'

import { DeepPartial } from '@apollo/client/utilities'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import {
  EmployeeTableViewRowFragment,
  ShiftManagementSwapShiftApprovableShiftDetailsFragment,
} from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'

export const formatShiftTime = (startAt: string, endAt: string) => {
  const start = stringToDate(startAt, 'backend_datetime')
  const end = stringToDate(endAt, 'backend_datetime')
  const startTime = i18nDateToString(start, 'ui_time')
  const endTime = i18nDateToString(end, 'ui_time')
  const date = i18nDateToString(start, 'ui_dmy_long')
  return `${startTime} - ${endTime}, ${date}`
}

export const useShiftDetails = (
  shift?:
    | ShiftManagementSwapShiftApprovableShiftDetailsFragment
    | DeepPartial<ShiftManagementSwapShiftApprovableShiftDetailsFragment>
    | null
) => {
  const employeeContent = useEmployeeTableViewRow({
    title: i18n.t('shift_management.view_options.titles.employee'),
    employee: shift?.employee?.employeeName ?? undefined,
  })

  if (!shift) return

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    employeeContent,
    shift.name && {
      title: i18n.t('shift_management.swap_shifts.shift'),
      content: {
        text: shift.name,
        type: 'item' as const,
      },
    },
    shift.startAt &&
      shift.endAt && {
        title: i18n.t('shift_management.view_options.titles.schedule'),
        content: {
          text: formatShiftTime(shift.startAt, shift.endAt),
          type: 'item' as const,
        },
      },
  ].filter(Boolean)

  return details
}

export const getEmployeeNameInfo = (
  employee?: DeepPartial<EmployeeTableViewRowFragment> | null
) => {
  if (!employee) return { preferredName: undefined, fullName: undefined }

  const preferredName = employee?.preferredName ?? employee?.firstName

  const fullName =
    employee.fullName && employee.lastName
      ? getEmployeeDisplayName({
          fullName: employee.fullName,
          lastName: employee.lastName,
          preferredName,
        })
      : undefined

  return {
    preferredName,
    fullName,
  }
}
